type JWTPayload = { exp?: number; [k: string]: any };

const LS = {
  access: 'access_token',
  id: 'id_token',
  user: 'userData',
  isAuth: 'isAuthenticated',
  lastActive: 'last_active_at',
  expAt: 'access_exp_at', // ms epoch
};

export const INACTIVITY_MS = 60 * 60 * 1000; 
export const INACTIVITY_WARNING_MS = 15 * 60 * 1000; 
const SKEW_MS = 60 * 1000; // 60s clock skew for exp check

function base64UrlDecode(input: string) {
  input = input.replace(/-/g, '+').replace(/_/g, '/');
  const pad = input.length % 4;
  if (pad) input += '='.repeat(4 - pad);
  return atob(input);
}

export function decodeJwtPayload(token?: string): JWTPayload | null {
  if (!token) return null;
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  try {
    const json = base64UrlDecode(parts[1]);
    return JSON.parse(json);
  } catch {
    return null;
  }
}

export function getTokens() {
  return {
    access: localStorage.getItem(LS.access),
    id: localStorage.getItem(LS.id),
  };
}

export function logout(reason?: string) {
  try {
    clearAuthStorage();
  } finally {
    if (reason) console.warn('[logout]', reason);
    window.location.replace('/login');
  }
}

export function saveAccessExpiryFromToken() {
  const { access } = getTokens();
  const payload = decodeJwtPayload(access || '');
  if (payload?.exp) {
    const ms = payload.exp * 1000;
    localStorage.setItem(LS.expAt, String(ms));
    return ms;
  }
  return null;
}

/** Clears auth-related keys without redirecting (safe for unload handlers). */
export function clearAuthStorage() {
  localStorage.removeItem(LS.access);
  localStorage.removeItem(LS.id);
  localStorage.removeItem(LS.user);
  localStorage.removeItem(LS.isAuth);
  localStorage.removeItem(LS.expAt);
  localStorage.removeItem(LS.lastActive);
}

export function isExpiredSoon(): boolean {
  const expAt = Number(localStorage.getItem(LS.expAt) || '0');
  if (!expAt) return true; // if we can't determine, be safe
  return Date.now() + SKEW_MS >= expAt;
}

export function markActivityNow() {
  localStorage.setItem(LS.lastActive, String(Date.now()));
  inactivityWarned = false;
}

export function isInactive(): boolean {
  const last = Number(localStorage.getItem(LS.lastActive) || '0');
  if (!last) return false; // first load
  return Date.now() - last > INACTIVITY_MS;
}

let watchdogTimer: number | undefined;
let inactivityWarned = false;

export function startSessionWatchdogs() {
  // record first activity
  markActivityNow();

  // listen to user activity
  const ACTIVITY_EVENTS = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart', 'visibilitychange'];
  const onActivity = () => markActivityNow();
  ACTIVITY_EVENTS.forEach(evt => window.addEventListener(evt, onActivity, { passive: true }));

  const tick = () => {
    // 1) expire/near-expire -> logout (based on actual token expiry, not inactivity)
    if (isExpiredSoon()) {
      logout('access token expired/expiring');
      return;
    }
    // Inactivity logout disabled - was causing issues with long-running uploads
    watchdogTimer = window.setTimeout(tick, 30 * 1000); // check every 30s
  };

  if (watchdogTimer) window.clearTimeout(watchdogTimer);
  tick();
}

/** Ensure auth is cleared if the tab/page is closed or navigated away.
 * 
 * NOTE: This function is currently disabled because it was causing users to be
 * logged out when refreshing the page or opening links in new tabs. The app
 * already has robust security measures in place:
 * - Token expiration checks (isExpiredSoon)
 * - Inactivity timeout (isInactive)
 * - Backend token validation
 * 
 * If we need to re-enable this, we have to consider using a more sophisticated approach
 * that can distinguish between actual tab close vs refresh/navigation.
 */
export function setupTabCloseAutoLogout() {
  // DISABLED: This was causing logout on refresh and new tab opens
  // The beforeunload/pagehide events fire in too many scenarios:
  // - Tab close (intended)
  // - Page refresh (unintended - causes logout)
  // - Opening link in new tab (unintended - causes logout)
  // - Navigation (unintended - causes logout)
  // 
  // Since localStorage is shared across tabs, clearing it affects all tabs.
  // 
  // If re-enabling is needed, consider:
  // 1. Using pagehide event with persisted flag
  // 2. Using sessionStorage flag to detect refresh vs close
  // 3. Only clearing on actual window close (hard to detect reliably)
  
  // const onUnload = () => {
  //   try {
  //     clearAuthStorage();
  //   } catch {}
  // };
  // window.addEventListener('pagehide', onUnload);
  // window.addEventListener('beforeunload', onUnload);
}


/** Fetch helper that attaches Authorization and logs out on 401/403,
 *  but allows opt-out for specific calls (like password verification).
 */
export async function apiFetch(
  input: RequestInfo,
  init: RequestInit & { skipAuthRedirect?: boolean } = {}
) {
  const access = localStorage.getItem(LS.access);
  const headers = new Headers(init.headers || {});
  if (access) headers.set('Authorization', `Bearer ${access}`);
  if (!headers.has('Content-Type') && !(init.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json');
  }

  // Mark activity whenever we hit API
  markActivityNow();

  const res = await fetch(input, { ...init, headers });

  // Normalize URL to make matching robust
  const url = typeof input === 'string' ? input : input.toString();
  const lowerUrl = url.toLowerCase();

  // ✅ Never auto-logout for verify-password endpoint (safe match)
  if (
    (res.status === 401 || res.status === 403) &&
    (lowerUrl.endsWith('/auth/verify-password') || lowerUrl.includes('/auth/verify-password?'))
  ) {
    return res; // let caller handle manually
  }

  // ✅ Never auto-logout for users/me endpoint during login
  if (
    (res.status === 401 || res.status === 403) &&
    (lowerUrl.endsWith('/users/me') || lowerUrl.includes('/users/me'))
  ) {
    return res; // let caller handle manually
  }

  // ✅ Respect skipAuthRedirect flag
  if ((res.status === 401 || res.status === 403) && !init.skipAuthRedirect) {
    logout('auth error from API');
    throw new Error('Unauthorized');
  }

  return res;
}
